import { StyledLabel, Wrapper } from './styled';
import { BlockProps } from './types';
import { FormattedAmount } from '../../../../../components';
import { ReservePriceLabel, VehicleSoldLabel } from '../../../../../components/ReservePriceMet';
import { CurrentBidComponent } from '../../../../../components/Typography';
import styled from 'styled-components';
import { useBidsOfferAuction } from '../hooks';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { OfferAuction, OfferAuctionStatus } from '../../../../../types/vehicle';
import { VEHICLE_STATUSES } from '../../../../../constants';
import HighestBidder from '../HighestBidder';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1px;
`;

const HighOfferContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const BoldText = styled.span`
  color: #000;
  font-size: 12px;
  font-weight: 500;
`;

type BlockInfoProps = BlockProps & {
  offerAuction?: OfferAuction | null;
};

type DisplayInfo = {
  bidInfo: [string, number];
  additionalInfo: [string, number | JSX.Element][];
  isAwarded?: boolean;
};

const useGetDisplayInfo = ({ currentVehicle }: BlockInfoProps): DisplayInfo => {
  const sellerPrice = currentVehicle.active_alternative_offer?.amount;
  const topBidOffer = currentVehicle.top_alternative_bid_offer?.amount;
  const myBidOffer = currentVehicle.my_alternative_bid_offer?.amount;
  const isHighBidder =
    currentVehicle.top_alternative_bid_offer?.user_id === currentVehicle.my_alternative_bid_offer?.user_id;

  const isMyBidBelowMinimum = myBidOffer && myBidOffer < currentVehicle.starting_bid;

  const [highOfferAmount, displayName] = (() => {
    return [topBidOffer, isHighBidder ? '/ (You)' : ''];
  })();

  const myBidOfferValue = myBidOffer ? (
    <HighOfferContainer>
      <FormattedAmount amount={myBidOffer} />
      {isMyBidBelowMinimum && <span style={{ color: '#3994DE' }}>{'(Below minimum)'}</span>}
    </HighOfferContainer>
  ) : (
    0
  );

  return {
    bidInfo: ['Buy Now For:', sellerPrice],
    additionalInfo: [
      ['Your Offer:', myBidOfferValue],
      [
        'High Offer:',
        highOfferAmount ? (
          <HighOfferContainer>
            <FormattedAmount amount={highOfferAmount} />
            <span>{displayName}</span>
          </HighOfferContainer>
        ) : (
          0
        )
      ]
    ],
    isAwarded: currentVehicle.status === 'awarded'
  };
};

export const AlternativeBidInfoBlock = ({ currentVehicle }: BlockProps) => {
  const offerAuction = useBidsOfferAuction(currentVehicle.id);

  const { bidInfo, additionalInfo, isAwarded } = useGetDisplayInfo({
    currentVehicle,
    offerAuction
  });

  const [label, sellerPrice] = bidInfo;

  return (
    <Container>
      <Wrapper justifyBetween>
        <StyledLabel isBold>{label}</StyledLabel>
        {sellerPrice ? <CurrentBidComponent amount={sellerPrice} /> : 'N/A'}
      </Wrapper>

      {additionalInfo.map(([label, amount]) => (
        <Wrapper justifyBetween>
          <StyledLabel>{label}</StyledLabel>
          {typeof amount === 'number' ? (
            <BoldText>{amount ? <FormattedAmount amount={amount} /> : '-'}</BoldText>
          ) : (
            <BoldText>{amount}</BoldText>
          )}
        </Wrapper>
      ))}
      {isAwarded ? (
        <VehicleSoldLabel activeVehicle={currentVehicle} />
      ) : (
        <ReservePriceLabel activeVehicle={currentVehicle} />
      )}
    </Container>
  );
};
